import HTTP from "@/service/HTTP";
import serviceFactory from "@/service/factory";
import notification from "@/notify";
import router from "@/router";
import Vue from "vue";
import _ from "lodash";

const faqs = new HTTP("/v1/faqs");
const DEPLOY_CHUNK_SIZE = 25;
const DEPLOY_CHUNK_TIMEOUT_MS = 120000;

const ENTITY_TYPE_TO_STATE_KEY = {
  BOOK: "books",
  CHAPTER: "chapters",
  BOOK_PACKAGE: "bookPackages",
  COMPETENCE_MODULE: "competenceModules",
  SCHOOL_SUBJECT: "schoolSubjects",
  SCHOOL_TYPE: "schoolTypes",
  SKILL: "skills",
  TASK: "tasks",
  TASK_TYPE: "taskTypes",
  TOPIC: "topics",
  FILTER: "filter",
  CONTENT_TYPE: "contentTypes",
};

export default {
  BASIC_FILTER_CHANGE({ commit }, filterObj) {
    Vue.$log.debug("BASIC_FILTER_CHANGE", filterObj);
    commit("BASIC_FILTER_MUTATION", filterObj);
  },
  LOAD_DASHBOARD({ commit }) {
    const query = { ...router.currentRoute.query, page: 1, pageSize: 30 };
    return serviceFactory.DASHBOARD.get(null, query)
      .then((res) => {
        commit("DASHBOARD_MUTATION", res.data);
      })
      .catch((e) => {
        notification.error(e.message);
      });
  },
  LOAD_TASKS({ commit }) {
    const query = { ...router.currentRoute.query, page: 1, pageSize: 30 };
    return serviceFactory.DASHBOARD.get(null, query)
      .then((res) => {
        commit("UPDATE_TASKS_FROM_DASHBOARD", res.data);
      })
      .catch((e) => {
        notification.error(e.message);
      });
  },
  LOAD_MORE_TASKS({ commit, state }) {
    if (state.loadingMoreTasks || !state.hasMoreTasks) {
      return Promise.resolve();
    }
    commit("SET_LOADING_MORE_TASKS", true);
    const nextPage = state.taskCurrentPage + 1;
    const query = {
      ...router.currentRoute.query,
      page: nextPage,
      pageSize: 30,
    };
    return serviceFactory.DASHBOARD.get(null, query)
      .then((res) => {
        commit("APPEND_TASKS_FROM_DASHBOARD", res.data);
      })
      .catch((e) => {
        commit("SET_LOADING_MORE_TASKS", false);
        notification.error(e.message);
      });
  },
  RELOAD_ENTITY_TYPE({ commit, dispatch }, entityType) {
    return dispatch("LOAD_DASHBOARD");
  },
  FILTER_TASKS({ commit, dispatch }, query) {
    Vue.$log.debug("FILTER_TASKS", query);
    commit("CHANGE_CURRENT_TASK_FILTER", query);
  },
  SAVE_ENTITY({ commit, dispatch }, pair) {
    return new Promise((resolve, reject) => {
      const service = pair.service;
      const entity = pair.entity;
      const entityType = pair.entityType;
      if (!entity.name) {
        const msg = "Objekt ohne Namen nicht speicherbar!";
        notification.error(msg);
        reject(new Error(msg));
        return;
      }
      service
        .post(entity)
        .then((res) => {
          dispatch("LOAD_DASHBOARD");
          resolve(res);
        })
        .catch((e) => {
          const msg = _.get(e, "response.data.message", "UNKNOWN ERROR");
          notification.error(msg);
          Vue.$log.debug(e);
          reject(e);
        });
    });
  },
  SAVE_ENTITIES({ commit, dispatch }, pair) {
    return new Promise((resolve, reject) => {
      const service = pair.service;
      const entities = pair.entities;
      const entityType = pair.entityType;
      service
        .postBatch(entities)
        .then((res) => {
          dispatch("LOAD_DASHBOARD");
          resolve(res);
        })
        .catch((e) => notification.error(e.message) && reject(e));
    });
  },
  SET_CURRENT_ENTITY({ commit }, pair) {
    commit("CHANGE_CURRENT_ENTITY_MUTATION", pair);
  },
  CHANGE_ASSIGNMENT({ commit }, event) {
    commit("CHANGE_CURRENT_ASSIGNMENT_MUTATION", event);
  },
  async DEPLOY_TASKS(
    { commit, dispatch },
    { tasksOrIds = [], env = "production", onProgress, shouldCancel } = {}
  ) {
    const service = new HTTP(`/v1/deployment/${env}/batch`);
    const nameById = new Map();
    const taskIds = tasksOrIds.map((t) => {
      if (typeof t === "string") {
        nameById.set(t, t);
        return t;
      }
      nameById.set(t.id, t.name || t.id);
      return t.id;
    });

    const putChunk = (chunk) =>
      service
        .put({ taskIds: chunk }, null, {
          silent: true,
          timeout: DEPLOY_CHUNK_TIMEOUT_MS,
        })
        .then((res) => _.get(res, "data", []));

    const chunks = [];
    for (let i = 0; i < taskIds.length; i += DEPLOY_CHUNK_SIZE) {
      chunks.push(taskIds.slice(i, i + DEPLOY_CHUNK_SIZE));
    }

    const results = [];
    for (let i = 0; i < chunks.length; i++) {
      if (shouldCancel && shouldCancel()) {
        const remaining = taskIds.slice(results.length);
        remaining.forEach((id) => {
          results.push({
            taskId: id,
            taskName: nameById.get(id) || id,
            success: false,
            error: "Abgebrochen",
          });
        });
        break;
      }

      const chunk = chunks[i];
      const from = i * DEPLOY_CHUNK_SIZE + 1;
      if (onProgress) {
        onProgress({
          chunkIndex: i + 1,
          chunkCount: chunks.length,
          from,
          to: from + chunk.length - 1,
          done: results.length,
          total: taskIds.length,
        });
      }

      let chunkResults;
      try {
        chunkResults = await putChunk(chunk);
      } catch (first) {
        Vue.$log.error(first);
        try {
          chunkResults = await putChunk(chunk);
        } catch (second) {
          Vue.$log.error(second);
          const msg = _.get(
            second,
            "response.data.message",
            second.message || "Netzwerkfehler"
          );
          chunkResults = chunk.map((id) => ({
            taskId: id,
            taskName: nameById.get(id) || id,
            success: false,
            error: msg,
          }));
        }
      }

      results.push(...(chunkResults || []));
      if (onProgress) {
        onProgress({
          chunkIndex: i + 1,
          chunkCount: chunks.length,
          from,
          to: from + chunk.length - 1,
          done: results.length,
          total: taskIds.length,
        });
      }
    }

    dispatch("LOAD_DASHBOARD");
    commit("SET_DEPLOYMENT_RESULTS", results);
    return results;
  },
  DEPLOY_TASK({ dispatch }, obj = { env: "", task: "", loadData: true }) {
    return new Promise((resolve, reject) => {
      const deploymentPromise = new HTTP(
        `/v1/deployment/${obj.env}/${obj.task}`
      );
      deploymentPromise
        .put()
        .then(() => {
          if (obj.loadData) {
            dispatch("LOAD_DASHBOARD").then(() => resolve(obj));
          } else {
            resolve(obj);
          }
        })
        .catch((e) => {
          Vue.$log.error(e);
          reject(e);
        });
    });
  },
  REMOVE_DEPLOYED_TASK({ dispatch }, obj) {
    return new Promise((resolve, reject) => {
      const deploymentPromise = new HTTP(
        `/v1/deployment/${obj.env}/${obj.task}`
      );
      deploymentPromise
        .delete()
        .then((res) => {
          resolve(res);
          dispatch("LOAD_DASHBOARD");
        })
        .catch((e) => {
          Vue.$log.error(e);
          reject(e);
        });
    });
  },
  CLEAR_DEPLOYMENT_RESULTS({ commit }) {
    commit("SET_DEPLOYMENT_RESULTS", []);
  },
  async LOAD_FAQS({ commit }) {
    const data = await faqs
      .get(null, { field: "order", sort: "ascending" })
      .then((res) => res.data);
    commit("SET_FAQS", data);
  },
};
